import React, {useContext} from 'react';
import {ObjectId} from 'bson';

import {usePowerSyncData} from './PowerSyncDataProvider';
import {PERMISSIONS, RELATION_TYPE} from '../Constants/AppConstants';
import {usePermission} from './PermissionProvider';
import {SubmissionRelation} from '../Models/PowerSyncModels/models';
import {usePowerSyncUser} from './PowerSyncUserProvider';
import {usePowerSyncQuery} from '@powersync/react';
import usePowerSyncWriteData from '../Hooks/usePowerSyncWriteData';

type SubmissionRelationProviderProps = {
  children: React.ReactNode;
};

type RelationContextValue = {
  updateSubmissionRelation: (
    submissionId: string,
    relationId: string,
    relationType: string,
  ) => void;
  getRelatedSubmissions: (id: string) => Promise<string[]>;
};

const RelationContext = React.createContext<RelationContextValue>(
  {} as RelationContextValue,
);

//This provider handles permissions regarding to items selected in a record in the Submissions table
//
const SubmissionRelationProvider: React.FunctionComponent<
  SubmissionRelationProviderProps
> = ({children}): React.ReactElement => {
  const {submissions, submissionRelations, people, projects, SQLUsers} =
    usePowerSyncData();

  const {deleteItems, createSubmissionRelation} = usePowerSyncWriteData();

  const user = usePowerSyncUser();

  const {hasPermission} = usePermission();

  //This method updates the relation table
  //It also  by type removes the previous relation if it exists
  const updateSubmissionRelation = async (
    submissionId: string,
    relationId: string,
    relationType: string,
  ) => {
    //Remove old submission relation if it exists
    let oldRelation = submissionRelations.find(
      x => x.submissionId === submissionId && x.relationType === relationType,
    );

    if (oldRelation) {
      await deleteItems([oldRelation._id], 'SubmissionRelations');
    }

    let newSubmissionRelation: SubmissionRelation = {
      _id: new ObjectId().toString(),
      partition: user.getUserInfo().orgId.toString(),
      relationId: relationId,
      submissionId: submissionId,
      relationType: relationType,
    };

    createSubmissionRelation(newSubmissionRelation);
  };

  //Get submissions related to the id arg
  const getRelatedSubmissions = async (id: string) => {
    let submissionIds: string[] = [];
    let isSupervisor = hasPermission(PERMISSIONS.SUPERVISOR);
    let isProjectManager = hasPermission(PERMISSIONS.PROJECT_MANAGER);

    let supervisedPeople = people.filter(
      x => x.supervisorId === id || x._id === id,
    );

    if (isSupervisor || isProjectManager) {
      // person involved
      let supervisedSubmissions = submissionRelations.filter(x =>
        supervisedPeople.map(x => x._id).includes(x.relationId!),
      );

      submissionIds = supervisedSubmissions.map(x => x.submissionId!);
    }

    if (isProjectManager) {
      //project
      let managedProjects: string[] = [];

      managedProjects = projects
        .filter(
          x =>
            x.projectManagerId === id ||
            x.safetyResourceId === id ||
            x._id === id,
        )
        .map(x => x._id);

      let managedSubmissions = submissionRelations.filter(x =>
        managedProjects.includes(x.relationId!),
      );

      submissionIds.push(...managedSubmissions.map(x => x.submissionId!));
    }

    if (isSupervisor) {
      //Userinvovled
      let supervisedUsers = SQLUsers.filter(x =>
        supervisedPeople.map(x => x.SQLServerId).includes(x.SQLServerPersonId),
      ).map(x => x._id);

      let supervisedSubmissions = submissionRelations.filter(x =>
        supervisedUsers.includes(x.relationId!),
      );

      submissionIds.push(...supervisedSubmissions.map(x => x.submissionId!));

      //Also add submissions that were created by people being supervised
      let supervisedPeopleEmails = supervisedPeople.map(x =>
        x.email?.toLowerCase(),
      );
      let createdBySupervisedPeople = submissions
        .filter(x => supervisedPeopleEmails.includes(x.createdBy.toLowerCase()))
        .map(x => x._id);

      submissionIds.push(...createdBySupervisedPeople);
    }

    const currentUser = user.getUserInfo();
    if (currentUser) {
      let sqlUser = SQLUsers.find(p => p.email === currentUser.email);

      if (sqlUser) {
        let userRelations = submissionRelations.filter(
          x =>
            x.relationId === sqlUser!._id &&
            x.relationType == RELATION_TYPE.USER_INVOLVED,
        );

        submissionIds.push(...userRelations.map(x => x.submissionId!));
      }
    }

    return submissionIds;
  };

  const value = {
    updateSubmissionRelation: updateSubmissionRelation,
    getRelatedSubmissions: getRelatedSubmissions,
  };

  return (
    <RelationContext.Provider value={value}>
      {children}
    </RelationContext.Provider>
  );
};

const useRelation = () => {
  const relationContext = useContext(RelationContext);

  if (relationContext === null)
    throw new Error('useRelation() called outside of a PermissionProvider?');

  return relationContext;
};

export {SubmissionRelationProvider, RelationContext, useRelation};
